/**
 * Removes Agent Component `params` keys that are irrelevant to the selected
 * provider/feature, so the persisted flowSpec is not polluted with defaults the
 * connector jsonSchema emits for every provider (see DST-4311).
 *
 * Content-driven and pure: the decision uses only the params themselves, so it
 * can run post-merge (after `mergeFormValueWithSchemaDefaults` re-inflates the
 * schema defaults) without any form/catalog context.
 *
 * Kept on purpose: the first-class feature toggles `enableRAG` / `detectPII`
 * (they carry meaning even when off); only their detail blocks are removed.
 */

import { GUARDRAILS_PII_PARAM_KEYS } from './agentConnectorReservedParamKeys';
import {
  ALL_MODEL_PARAM_KEYS,
  RAG_PARAM_KEYS
} from './formDataMapper/constants';
import { ALL_MODELS } from '../types/model.types';

const PROVIDER_TO_SLOT: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  ALL_MODELS.forEach(model => {
    if (!map.has(model.provider)) {
      map.set(model.provider, model.paramKey);
    }
  });
  return map;
})();

function resolveSelectedModelSlot(
  params: Record<string, unknown>
): string | undefined {
  if (params.useCustomModel === true) return 'customModel';
  const provider =
    typeof params.llmProvider === 'string'
      ? params.llmProvider.toLowerCase()
      : '';
  return PROVIDER_TO_SLOT.get(provider);
}

export function pruneIrrelevantAgentConnectorParams(
  params: Record<string, unknown>
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...params };

  const selectedSlot = resolveSelectedModelSlot(params);
  if (selectedSlot) {
    ALL_MODEL_PARAM_KEYS.forEach(key => {
      if (key !== selectedSlot) delete next[key];
    });
  }

  if (params.enableRAG !== true) {
    RAG_PARAM_KEYS.forEach(key => delete next[key]);
  }

  if (params.detectPII !== true) {
    GUARDRAILS_PII_PARAM_KEYS.forEach(key => delete next[key]);
  }

  return next;
}
